import { CommonModule, NgOptimizedImage } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, inject, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterOutlet } from '@angular/router';
import { SpriFullscreenComponent } from '@spri/components/fullscreen';
import { SpriLoadingBarComponent } from '@spri/components/loading-bar';
import { SpriNavigationService, SpriVerticalNavigationComponent } from '@spri/components/navigation';
import { SpriMediaWatcherService } from '@spri/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import type { Navigation } from 'app/core/navigation/navigation.types';
import { TenantContextService } from 'app/core/services/tenant-context.util';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { ShortcutsComponent } from 'app/layout/common/shortcuts/shortcuts.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'spri-thin-layout',
  templateUrl: './thin.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    NgOptimizedImage,
    SpriLoadingBarComponent,
    SpriVerticalNavigationComponent,
    MatButtonModule,
    MatIconModule,
    SpriFullscreenComponent,
    SearchComponent,
    ShortcutsComponent,
    NotificationsComponent,
    UserComponent,
    RouterOutlet,
  ],
})
export class ThinLayoutComponent implements OnInit, OnDestroy {
  protected readonly tenantContextService = inject(TenantContextService);
  public isScreenSmall: boolean;
  public navigation: Navigation;
  private readonly unsubscribeAll = new Subject<void>();

  /**
   * Constructor
   */
  private readonly navigationService = inject(NavigationService);
  private readonly spriMediaWatcherService = inject(SpriMediaWatcherService);
  private readonly spriNavigationService = inject(SpriNavigationService);

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  public ngOnInit(): void {
    // Subscribe to navigation data
    this.navigationService.navigation$.pipe(takeUntil(this.unsubscribeAll)).subscribe((navigation: Navigation) => {
      this.navigation = navigation;
    });

    // Subscribe to media changes
    this.spriMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this.unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes('md');
      });
  }

  /**
   * On destroy
   */
  public ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.unsubscribeAll.next(null);
    this.unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle navigation
   *
   * @param name
   */
  public toggleNavigation(name: string): void {
    // Get the navigation
    const navigation = this.spriNavigationService.getComponent<SpriVerticalNavigationComponent>(name);

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }
}
